import { useEffect } from "react";
import { styled } from "styled-components";

import { InfinityLoader, SectionTitleUnderlined, StyledGrid } from "common/components";
import { mediaStyles } from "common/styles";
import { useSelector } from "common/utilities/react-redux";
import type { TopicBackLabelData } from "context/session/sessionTypes";
import { getSiteLanguage } from "domains/language/selectors";
import { TopicTile } from "domains/topic/components/topicTile/TopicTile";
import type { Topic } from "entities/topics/types";

const StyledInnerContainer = styled.div`
  box-sizing: border-box;
  ${mediaStyles(({ spacing }) => `padding: 0 ${spacing}rem ${spacing}rem ${spacing}rem;`)};
`;

type CustomTopicGridProps = {
  fetchTopics: (shouldInvalidate: boolean) => void;
  isFetching: boolean;
  isPossibleNextPage: boolean;
  title: string;
  titleDataTestId?: string;
  topicBack: TopicBackLabelData;
  topics: Topic[];
};

export const CustomTopicGrid = ({
  fetchTopics,
  isFetching,
  isPossibleNextPage,
  title,
  titleDataTestId,
  topicBack,
  topics,
}: CustomTopicGridProps) => {
  const siteLanguage = useSelector(getSiteLanguage);

  useEffect(() => {
    fetchTopics(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteLanguage]);

  return (
    <StyledInnerContainer>
      <SectionTitleUnderlined dataTestId={titleDataTestId}>{title}</SectionTitleUnderlined>
      <InfinityLoader isPending={isFetching} loadNextPage={() => fetchTopics(false)} hasNextPage={isPossibleNextPage}>
        <StyledGrid>
          {topics.map((topic) => (
            <TopicTile
              key={topic.id}
              topicId={topic.id}
              topicName={topic.name}
              provider={topic.provider}
              topicBack={topicBack}
              attachment={topic.attachment ?? undefined}
              canEdit={topic.can_edit ?? false}
              isCustom={topic.is_custom ?? false}
              isNew={topic.is_new ?? false}
              isFollowing={topic.is_following ?? false}
              imageUrl={topic.images?.x_small}
            />
          ))}
        </StyledGrid>
      </InfinityLoader>
    </StyledInnerContainer>
  );
};
